import { ICustomColumnDefinitionInput } from '../../../../utils/apollo/graphqlSchema';
import { useCallback, useMemo } from 'react';
import {
  DerivedDefinitionType,
  IDerivedColumnDraft,
} from '../../../../redux/slices/dataModeler';
import { isNil } from 'lodash';
import { rawSqlValidator, validationErrorToString } from '../validation';
import { UnreachableCaseError } from 'ts-essentials';

interface IProps {
  definition: ICustomColumnDefinitionInput | null;
  definitionType: DerivedDefinitionType | null;
  updateDefinition: (props: Partial<IDerivedColumnDraft>) => void;
  resetPreviewData: () => void;
  hasColumnNameError: boolean;
}

const useDerivedColumnDefinitionModel = ({
  definition,
  definitionType,
  updateDefinition,
  resetPreviewData,
  hasColumnNameError,
}: IProps) => {
  const rawSqlInputOrDefault = useMemo(
    () =>
      definition?.rawSql ?? {
        rawSql: '',
        fullDataRequired: false,
      },
    [definition?.rawSql],
  );

  const mappedStringInputOrDefault = useMemo(
    () =>
      definition?.mappedString ?? {
        mappedColumn: '',
        mapNull: null,
        mapElse: null,
        values: [
          {
            original: '',
            mapped: '',
          },
        ],
      },
    [definition?.mappedString],
  );

  const setSelectedDefinitionType = useCallback(
    (newType: DerivedDefinitionType) => {
      resetPreviewData();
      switch (newType) {
        case 'RawSql':
          const rawSqlDef: ICustomColumnDefinitionInput = {
            rawSql: rawSqlInputOrDefault,
            mappedString: definition?.mappedString ?? null,
          };
          updateDefinition({
            definitionType: newType,
            definition: rawSqlDef,
          });
          break;
        case 'MappedString':
          const mappedStrDef: ICustomColumnDefinitionInput = {
            rawSql: definition?.rawSql ?? null,
            mappedString: mappedStringInputOrDefault,
          };
          updateDefinition({
            definitionType: newType,
            definition: mappedStrDef,
          });
          break;
        default:
          throw new UnreachableCaseError(newType);
      }
    },
    [
      definition?.mappedString,
      definition?.rawSql,
      mappedStringInputOrDefault,
      rawSqlInputOrDefault,
      updateDefinition,
      resetPreviewData,
    ],
  );

  const canConfirmMappedString =
    !!definition?.mappedString &&
    !!definition.mappedString.mappedColumn &&
    (definition.mappedString.values.some(
      ({ original, mapped }) => !!original || !!mapped,
    ) ||
      !!definition.mappedString.mapNull);

  const rawSqlValidationError = useMemo(() => {
    const currentValue = definition?.rawSql?.rawSql;
    if (isNil(currentValue)) {
      return null;
    }
    const parsed = rawSqlValidator.safeParse(currentValue);
    if (parsed.success) {
      return null;
    } else {
      return validationErrorToString(parsed.error);
    }
  }, [definition?.rawSql?.rawSql]);

  const canConfirmRawSql =
    !!definition?.rawSql?.rawSql && !rawSqlValidationError;

  const canConfirm =
    !hasColumnNameError &&
    ((definitionType === 'MappedString' && canConfirmMappedString) ||
      (definitionType === 'RawSql' && canConfirmRawSql));

  return {
    canConfirm,
    setSelectedDefinitionType,
    rawSqlValidationError,
  };
};

export default useDerivedColumnDefinitionModel;
